import React, { useState } from 'react';
import { Input, RadioField, TextField } from '@kaizen/draft-form';
import { SelectOption, CheckedOption } from '../../types';
import { OTHER_OPTION_ID } from '../../constants';
import selectQuestionStyles from '../SelectQuestion.scss';

export type Props = {
  questionId: string;
  questionHeaderId: string;
  questionText: string;
  options: SelectOption[];
  existingSelection: string | null;
  existingOtherValue: string | null;
  onSave: (
    checkedOptions: CheckedOption[],
    otherValue: string,
    checkedOption: string,
    answered: boolean
  ) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  isPDF: boolean;
  validationError: boolean;
  validationErrorId: string;
  uiText: {
    otherLabel: string;
    forLabel: string;
  };
  radioThreshold: number;
  questionMandatory: boolean;
};

const SingleSelectRadios = (props: Props): React$Element<string> => {
  const {
    questionId,
    questionHeaderId,
    questionText,
    options,
    existingOtherValue,
    existingSelection,
    onSave,
    containerRef,
    isPDF,
    validationError,
    validationErrorId,
    uiText,
    radioThreshold,
    questionMandatory,
  } = props;
  const [selectedOption, setSelected] = useState<string | null>(
    existingSelection
  );
  const otherCheckedStatus = selectedOption === OTHER_OPTION_ID;
  const [showOtherInput, setShowOtherInput] =
    useState<boolean>(otherCheckedStatus);
  const [otherValue, setOtherValue] = useState<string>(
    existingOtherValue || ''
  );

  const uncheck = (option: SelectOption) => {
    setShowOtherInput(false);
    setSelected(null);
    onSave(
      [
        { id: option.value, checked: false },
        { id: 'other_option', checked: false },
      ],
      otherValue,
      option.value,
      false
    );

    // Update the record of the selected options still living in Haml, for dependent question functionality
    $(`input[data-select-option="${selectedOption}"]`).val('false');

    // Send back to jQuery for some adjustments to the .question container that we can't make here yet
    $(document).trigger('reactAnswerChange', {
      container: containerRef.current,
      answered: false,
    });
  };

  const onCheck = (optionId: string) => {
    const oldValue = selectedOption;
    setShowOtherInput(optionId === OTHER_OPTION_ID);
    setSelected(optionId);

    // Send request
    let optionValues;
    if (optionId === OTHER_OPTION_ID) {
      optionValues = [{ id: OTHER_OPTION_ID, checked: true }];
    } else {
      optionValues = [
        { id: OTHER_OPTION_ID, checked: false },
        { id: optionId, checked: true },
      ];
    }

    onSave(optionValues, otherValue, optionId, true);

    // Update the record of the selected options still living in Haml, for dependent question functionality
    if (selectedOption !== null) {
      $(`input[data-select-option="${oldValue}"]`).val('false');
    }
    $(`input[data-select-option="${optionId}"]`).val('true');

    // Send back to jQuery for some adjustments to the .question container that we can't make here yet
    $(document).trigger('reactAnswerChange', {
      container: containerRef.current,
      answered: true,
    });
  };

  const onOtherChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtherValue(event.target.value);
  };

  const onOtherBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onSave(
      selectedOption ? [{ id: selectedOption, checked: true }] : [],
      event.target.value,
      'text_value',
      true
    );
  };

  if (isPDF && options.length > radioThreshold) {
    return (
      <PDFTooManyOptions
        questionId={questionId}
        options={options}
        radioThreshold={radioThreshold}
      />
    );
  }

  return (
    <div
      role="radiogroup"
      aria-labelledby={questionHeaderId}
      aria-describedby={(validationError && validationErrorId) || undefined}
      data-focus-to-on-navigate="true"
      tabIndex={-1}
      className={selectQuestionStyles.inputGroup}
      aria-required={questionMandatory}
    >
      {options.map((option) => {
        const checkedStatus = selectedOption === option.value;
        const showInput =
          option.value === OTHER_OPTION_ID && (showOtherInput || isPDF);
        return (
          <div
            key={option.value}
            data-automation-id={`select-option-wrapper-single-${option.value}`}
            data-automation-selected={checkedStatus}
          >
            <div
              data-automation-id={`select-option-single-intercept-${option.value}`}
              onClickCapture={(event: React.MouseEvent<HTMLDivElement>) => {
                // This is non-standard radio button behaviour, but we want to
                // allow users to remove their answer for a question if they
                // decide they didn’t really want to answer it
                if (checkedStatus) {
                  event.preventDefault();
                  uncheck(option);
                }
              }}
              onKeyDownCapture={(
                event: React.KeyboardEvent<HTMLDivElement>
              ) => {
                const { keyCode } = event;
                // Only fire if pressed key is space
                if (keyCode === 32 && checkedStatus) {
                  event.preventDefault();
                  uncheck(option);
                }
              }}
            >
              <RadioField
                id={`${questionId}-${option.value}`}
                name={`single-select-${questionId}`}
                value={option.value}
                labelText={option.label}
                onChange={() => onCheck(option.value)}
                selectedStatus={checkedStatus}
                // This aria-label _shouldn't_ be required, but there's something weird going on.
                // Windows/Chromium is having trouble determining the accessible name from the label in this environment.
                // See https://cultureamp.slack.com/archives/C0405G8EJ48/p1696896101605489 for more information.
                aria-label={option.label}
              />
            </div>
            {showInput && (
              <div
                className={selectQuestionStyles.otherInputContainer}
                data-automation-id={`select-option-single-other-input`}
              >
                <TextField
                  id={`${questionId}-other-input`}
                  labelText={uiText.otherLabel}
                  value={otherValue}
                  onChange={onOtherChange}
                  onBlur={onOtherBlur}
                  aria-label={`${option.label} ${uiText.otherLabel} ${uiText.forLabel} ${questionText}`}
                  data-automation-id={`other-input-single-${questionId}`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

type PDFTooManyOptionsProps = {
  questionId: string;
  options: SelectOption[];
  radioThreshold: number;
};

export const PDFTooManyOptions = (
  props: PDFTooManyOptionsProps
): React$Element<string> => {
  const { questionId, options, radioThreshold } = props;
  return (
    <div>
      {options.slice(0, radioThreshold).map((option) => {
        return (
          <div
            key={option.value}
            data-automation-id={`select-option-single-${option.value}`}
          >
            <RadioField
              id={option.value}
              name={`single-select-${questionId}`}
              value={option.value}
              labelText={option.label}
            />
          </div>
        );
      })}
      <div>
        &hellip; {options.length - radioThreshold} other answers are not shown.
      </div>
    </div>
  );
};

export default SingleSelectRadios;
